import { storageKey, storageKeyWithSuffix } from '@/lib/config/brand'

/**
 * Storage Keys Registry
 *
 * Centralized registry of all localStorage keys.
 * Uses brand abstraction for easy rebranding.
 *
 * Usage:
 *   import { StorageKeys } from '@/lib/storage/keys'
 *   const [value, setValue] = usePersistedState(StorageKeys.UI_SHOW_SIDEBAR, true)
 */

export const StorageKeys = {
  // UI Layout
  UI_SHOW_SIDEBAR: storageKey('ui_show_sidebar'),
  UI_SIDEBAR_COLLAPSED: storageKey('ui_sidebar_collapsed'),
  /** Auto-hide the sidebar to the rail. Off by default. Flyout is not persisted. */
  UI_SIDEBAR_AUTO_HIDE: storageKey('ui_sidebar_auto_hide'),
  UI_SHOW_AGENT: storageKey('ui_show_agent'),
  UI_AGENT_COLLAPSED: storageKey('ui_agent_collapsed'),
  UI_SHOW_DOCUMENT_LIST: storageKey('ui_show_document_list'),
  UI_PANEL_SIZES: storageKey('ui_panel_sizes'),
  UI_EXPANDED_LEFT_SIZE: storageKey('ui_expanded_left_size'),
  UI_EXPANDED_RIGHT_SIZE: storageKey('ui_expanded_right_size'),
  /** Secondary dock content enter style - gallery #panel-motion */
  UI_SHELL_PANEL_MOTION_STYLE: storageKey('ui_shell_panel_motion_style'),

  // Documents & Tabs
  DOCUMENTS_OPEN_TABS: storageKey('documents_open_tabs'),
  DOCUMENTS_OPEN_TABS_WINDOW: (windowId: string) => storageKeyWithSuffix('documents_open_tabs', windowId),
  DOCUMENTS_ACTIVE_TAB: storageKey('documents_active_tab'),
  DOCUMENTS_ACTIVE_TAB_WINDOW: (windowId: string) => storageKeyWithSuffix('documents_active_tab', windowId),
  /** workspaceId -> last active tab id while that workspace was in scope */
  DOCUMENTS_LAST_ACTIVE_TAB_BY_WORKSPACE: storageKey('documents_last_active_tab_by_workspace'),
  DOCUMENTS_SELECTED_PROJECT: storageKey('documents_selected_project'),
  DOCUMENTS_ACTIVE_SIDEBAR_TAB: storageKey('documents_active_sidebar_tab'),
  DOCUMENTS_FOLDER_VIEW_MODE: storageKey('documents_folder_view_mode'),
  SEARCH_RESULTS_VIEW_MODE: storageKey('search_results_view_mode'),
  DOCUMENTS_PANEL_SIZES: storageKey('documents_panel_sizes'),
  VOICE_MEMOS_VIEW_MODE: storageKey('voice_memos_view_mode'),

  // Editor
  EDITOR_MODE: storageKey('editor_mode'),
  /** Reading column vs full-width prose in document viewer (TipTap + preview). */
  PAGE_WIDTH_MODE: storageKey('page_width_mode'),
  EDITOR_DIFF_VIEW_MODE: storageKey('editor_diff_view_mode'),
  /** Per-document-kind preference: show YAML frontmatter in Source mode (see source-view-frontmatter bet). */
  SOURCE_FRONTMATTER_PREFS: storageKey('source_frontmatter_prefs'),
  /** Opt-in reading bookmarks (heading gutter + overflow menu actions). Default off. */
  EDITOR_SOFT_BOOKMARKS_ENABLED: storageKey('editor_soft_bookmarks_enabled'),

  // AI/Chat
  /** When true, Chats sidebar opens conversations in editor tabs by default. */
  AGENTS_OPEN_IN_EDITOR: storageKey('agents_open_in_editor'),
  /** Last real agent-panel thread id (session); restored when refocusing the panel. */
  AGENT_PANEL_LAST_THREAD_ID: storageKey('agent_panel_last_thread_id'),
  /** Tags for that last thread so restore survives a full reload (in-memory cache is empty). */
  AGENT_PANEL_LAST_THREAD_META: storageKey('agent_panel_last_thread_meta'),
  AI_SELECTED_PROVIDER: storageKey('ai_selected_provider'),
  AI_LAST_SELECTED_MODEL: storageKey('ai_last_selected_model'), // Last model chosen in the chat picker (takes priority over server settings)
  AI_SELECTED_MODEL: (provider: string) => storageKeyWithSuffix('ai_selected_model', provider), // Legacy per-provider storage
  AI_ENABLED_PROVIDERS: storageKey('ai_enabled_providers'), // Array of enabled provider IDs
  /** Ask / Plan / Agent chat mode; per-user when userId is provided. */
  CHAT_MODE: (userId?: string) =>
    userId ? storageKeyWithSuffix('chat_mode', userId) : storageKey('chat_mode'),

  // Graph View
  GRAPH_SIMILARITY: storageKey('graph_similarity'),
  GRAPH_MANUAL_LINKS: storageKey('graph_manual_links'),
  GRAPH_AI_CONNECTIONS: storageKey('graph_ai_connections'),
  GRAPH_FLOW_JOBS: storageKey('graph_flow_jobs'),
  GRAPH_GROUP_BY: storageKey('graph_group_by'),
  GRAPH_FILTER_PROJECT: storageKey('graph_filter_project'),

  // Basecamp Integration
  BASECAMP_BOOKMARKS_EXPANDED: storageKey('basecamp_bookmarks_expanded'),
  BASECAMP_MY_STUFF_EXPANDED: storageKey('basecamp_my_stuff_expanded'),
  BASECAMP_ASSIGNMENTS_BOOKMARKED_ONLY: storageKey('basecamp_assignments_bookmarked_only'),
  BASECAMP_HIDDEN_PROJECTS: storageKey('basecamp_hidden_projects'),
  BASECAMP_PINNED_PROJECTS: storageKey('basecamp_pinned_projects'),
  BASECAMP_PROJECTS_EXPANDED: storageKey('basecamp_projects_expanded'),
  BASECAMP_SIDEBAR_WIDGET_SIZES: storageKey('basecamp_sidebar_widget_sizes'),
  BASECAMP_SYNC_PAUSED: storageKey('basecamp_sync_paused'),
  /** Todos index filter blob. Optional org suffix. */
  BASECAMP_TODOS_VIEW: (orgId?: string) =>
    orgId
      ? storageKeyWithSuffix('basecamp_todos_view', orgId)
      : storageKey('basecamp_todos_view'),
  /** Activity index filter blob. Optional org suffix. */
  BASECAMP_ACTIVITY_VIEW: (orgId?: string) =>
    orgId
      ? storageKeyWithSuffix('basecamp_activity_view', orgId)
      : storageKey('basecamp_activity_view'),

  // Browse Sidebar
  BROWSE_EXPANDED_WORKSPACES: storageKey('browse_expanded_workspaces'),
  BROWSE_EXPANDED_EXTENSIONS: storageKey('browse_expanded_extensions'),
  BROWSE_EXPANDED_FOLDER_NODES: storageKey('browse_expanded_folder_nodes'),
  /** Per explorer tree (`${wsId}::${spaceId}`): folder name segments from that tree's root for drill-down. */
  BROWSE_DOCUMENTS_FOCUS_SEGMENTS: storageKey('browse_documents_focus_segments'),
  BROWSE_PANEL_SIZES: storageKey('browse_panel_sizes'),
  /**
   * Open Tabs user max height in px (ceiling). Absent/null = auto
   * (hug content up to soft max). Double-click resize handle clears.
   */
  BROWSE_OPEN_TABS_USER_MAX_PX: storageKey('browse_open_tabs_user_max_px'),
  /** Browse sidebar: Open Tabs list collapsed to header-only (chevron). */
  BROWSE_OPEN_TABS_COLLAPSED: storageKey('browse_open_tabs_collapsed'),
  /** Left sidebar: show Open Tabs section above the active view (Flows/Inbox/Browse). */
  SIDEBAR_OPEN_TABS_VISIBLE: storageKey('sidebar_open_tabs_visible'),
  /** Sidebar list density: `dense` | `comfortable`. */
  SIDEBAR_LIST_DENSITY: storageKey('sidebar_list_density'),
  /** workspaceId -> last active timestamp (MRU pip ordering). */
  WORKSPACE_LAST_ACTIVE_AT: storageKey('workspace_last_active_at'),
  /** Browse sidebar: Workspaces section collapsed to header-only (chevron). */
  BROWSE_WORKSPACES_SECTION_COLLAPSED: storageKey('browse_workspaces_section_collapsed'),
  /** Browse sidebar: Google Drive Imports section collapsed. */
  BROWSE_DRIVE_IMPORTS_COLLAPSED: storageKey('browse_drive_imports_collapsed'),
  /** Focus browse: hide inactive client folders in Documents tree. */
  BROWSE_FOCUS_ENABLED: storageKey('browse_focus_enabled'),
  BROWSE_FOCUS_INACTIVE_DAYS: storageKey('browse_focus_inactive_days'),
  BROWSE_FOCUS_MIN_CHILDREN: storageKey('browse_focus_min_children'),
  BROWSE_FOCUS_MAX_VISIBLE: storageKey('browse_focus_max_visible'),
  BROWSE_SHOW_ACTIVITY_IN_TREE: storageKey('browse_show_activity_in_tree'),
  /** { alwaysShowPaths: string[], hiddenManualPaths: string[] } */
  BROWSE_FOLDER_PREFS: storageKey('browse_folder_prefs'),

  /** Last SMTP Send-test recipient string + recent addresses (chrome only). */
  SMTP_TEST_RECIPIENTS: storageKey('smtp_test_recipients'),


  // Inbox
  INBOX_ITEMS: storageKey('inbox_items'),

  // Product tour (first-run + Help replay)
  PRODUCT_TOUR_COMPLETED: storageKey('product_tour_completed'),

  // Theme
  THEME: storageKey('theme'),

  // Command Palette
  COMMAND_PALETTE_SEARCH_HISTORY: storageKey('command_palette_search_history'),

  // Temporary/Backup (not persisted long-term)
  BACKUP_DOCUMENT: (documentId: string) => storageKeyWithSuffix('backup', documentId),
} as const

